import { useCallback, useEffect, useRef, useState } from "react";
import type { Card } from "../../../../packages/shared/src/types";
import { api } from "./api";
interface Page {
  cards?: Card[];
  items?: Card[];
  nextCursor: string | null;
  total: number;
  actionCount: number;
}
export function useCardCollection(
  direction: "received" | "sent",
  enabled: boolean,
  query: string,
) {
  const [state, setState] = useState({
    cards: [] as Card[],
    cursor: null as string | null,
    total: 0,
    actionCount: 0,
    loading: enabled,
    more: false,
    error: null as Error | null,
  });
  const sequence = useRef(0),
    loadingMore = useRef(false);
  const endpoint = direction === "sent" ? "/api/cards" : "/api/cards/received";
  const refresh = useCallback(async () => {
    const version = ++sequence.current;
    if (!enabled) {
      setState({
        cards: [],
        cursor: null,
        total: 0,
        actionCount: 0,
        loading: false,
        more: false,
        error: null,
      });
      return;
    }
    setState((previous) => ({ ...previous, loading: true, error: null }));
    try {
      const page = await api<Page>(
        `${endpoint}?q=${encodeURIComponent(query)}`,
      );
      if (sequence.current === version)
        setState({
          cards: page.cards || page.items || [],
          cursor: page.nextCursor,
          total: page.total,
          actionCount: page.actionCount,
          loading: false,
          more: false,
          error: null,
        });
    } catch (error) {
      if (sequence.current === version)
        setState((previous) => ({
          ...previous,
          loading: false,
          error: error as Error,
        }));
    }
  }, [enabled, endpoint, query]);
  useEffect(() => {
    setState((previous) => ({
      ...previous,
      cards: [],
      cursor: null,
      total: 0,
      actionCount: 0,
      loading: enabled,
      more: false,
      error: null,
    }));
    const timer = setTimeout(() => void refresh(), query ? 250 : 0);
    const resume = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    addEventListener("focus", resume);
    document.addEventListener("visibilitychange", resume);
    return () => {
      clearTimeout(timer);
      sequence.current++;
      removeEventListener("focus", resume);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [refresh]);
  async function more() {
    if (!state.cursor || loadingMore.current || state.loading) return;
    const version = sequence.current;
    loadingMore.current = true;
    setState((previous) => ({ ...previous, more: true, error: null }));
    try {
      const page = await api<Page>(
        `${endpoint}?q=${encodeURIComponent(query)}&cursor=${encodeURIComponent(state.cursor)}`,
      );
      if (sequence.current === version)
        setState((previous) => ({
          ...previous,
          cards: [
            ...new Map(
              [...previous.cards, ...(page.cards || page.items || [])].map(
                (card) => [card.id, card],
              ),
            ).values(),
          ],
          cursor: page.nextCursor,
          total: page.total,
          actionCount: page.actionCount,
        }));
    } catch (error) {
      if (sequence.current === version)
        setState((previous) => ({ ...previous, error: error as Error }));
    } finally {
      loadingMore.current = false;
      if (sequence.current === version)
        setState((previous) => ({ ...previous, more: false }));
    }
  }
  function update(card: Card) {
    sequence.current++;
    setState((previous) => ({
      ...previous,
      cards: previous.cards.map((item) => (item.id === card.id ? card : item)),
    }));
    void refresh();
  }
  return { ...state, refresh, moreCards: more, update };
}
